import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Send, Download, CreditCard, TrendingUp, History, Users, Wallet, RefreshCw, X, Mail, Copy, CheckCheck } from 'lucide-react';
import { formatCurrency, calculateWithdrawalDays } from '../utils/helpers';
import { useUser } from '../context/UserContext';
import DashboardLayout from '../components/DashboardLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const { 
    user: contextUser, 
    refreshUserData, 
    history: userHistory,
    transferHistory: userTransferHistory,
    balance,
    getFormattedBalance,
  } = useUser();
  
  const [user, setUser] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [withdrawalDays, setWithdrawalDays] = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [stripeChecked, setStripeChecked] = useState(false);
  const [anyApproved,   setAnyApproved]   = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // Refresh user data on mount and every 30 seconds
  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }
    
    setUser(contextUser);
    
    if (contextUser.dateCreated) {
      const days = calculateWithdrawalDays(contextUser.dateCreated);
      setWithdrawalDays(days);
    }

    setLoading(false);

    // Check if user has at least one approved Stripe account
    fetch(`https://tradebot-k4nt.onrender.com/api/payment/retive-stripe-account-info/web/${contextUser.telegramName}`)
      .then(r => r.json())
      .then(data => {
        const obj = data?.allAccountObj;
        if (obj) {
          const isApproved = (v) => v && v !== 2 && typeof v === 'object' && v.charges_enabled && v.payouts_enabled;
          setAnyApproved(isApproved(obj.retrieveUSD) || isApproved(obj.retrieveGBP) || isApproved(obj.retrieveYEN));
        }
        setStripeChecked(true);
      })
      .catch(() => setStripeChecked(true));

    // Auto-refresh every 30 seconds to keep data fresh
    const intervalId = setInterval(() => {
      refreshUserData(null, null, true); // Silent refresh
    }, 30000);

    // Initial refresh
    refreshUserData(null, null, true);

    return () => clearInterval(intervalId);
  }, [contextUser, navigate, refreshUserData]);

  const handleRefresh = async () => {
    if (!contextUser?.pin) return;
    setRefreshing(true);
    await refreshUserData(contextUser.telegramName, contextUser.pin, true);
    setRefreshing(false);
  };

  // Calculate this month's statistics from context data
  const calculateMonthStats = () => {
    if (!userTransferHistory) return { sent: 0, received: 0 };

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    let sent = 0;
    let received = 0;

    // Calculate from transfer history
    userTransferHistory.forEach(transfer => {
      const transferDate = new Date(transfer.date);
      if (transferDate.getMonth() === currentMonth && transferDate.getFullYear() === currentYear) {
        if (transfer.sender === user?.telegramName) {
          sent += parseFloat(transfer.amount);
        } else if (transfer.receiver === user?.telegramName) {
          received += parseFloat(transfer.amount);
        }
      }
    });

    return { sent, received };
  };

  // Get recent transactions (last 5) from context data
  const getRecentTransactions = () => {
    if (!userHistory && !userTransferHistory) return [];

    const allTransactions = [];

    // Add deposits and withdrawals from user history
    userHistory.forEach(item => {
      if (item.transactionType === 'deposit') {
        allTransactions.push({
          type: 'credit',
          description: 'Deposit',
          amount: item.amount,
          date: new Date(item.date).toLocaleDateString(),
          status: item.transactionProcess
        });
      } else if (item.transactionType === 'withdrawal') {
        allTransactions.push({
          type: 'debit',
          description: 'Withdrawal',
          amount: item.amount,
          date: new Date(item.date).toLocaleDateString(),
          status: item.transactionProcess
        });
      }
    });

    // Add transfers
    userTransferHistory.forEach(transfer => {
      if (transfer.sender === user?.telegramName) {
        allTransactions.push({
          type: 'debit',
          description: `Sent to ${transfer.receiver}`,
          amount: transfer.amount,
          date: new Date(transfer.date).toLocaleDateString(),
          status: 'success'
        });
      } else if (transfer.receiver === user?.telegramName) {
        allTransactions.push({
          type: 'credit',
          description: `Received from ${transfer.sender}`,
          amount: transfer.amount,
          date: new Date(transfer.date).toLocaleDateString(),
          status: 'success'
        });
      }
    });

    // Sort by date (newest first) and return last 5
    return allTransactions
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  };

  // Calculate total transactions count from context data
  const getTotalTransactions = () => {
    const deposits = userHistory?.length || 0;
    const transfers = userTransferHistory?.length || 0;
    return deposits + transfers;
  };

  if (!user) return null;

  const monthStats = calculateMonthStats();
  const SUPPORT_EMAIL = "modanmic5302023@gmail.com";

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(SUPPORT_EMAIL).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    });
  };

  const recentTransactions = getRecentTransactions();
  const totalTransactions = getTotalTransactions();

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Welcome back, {user.telegramName}! 👋
            </h1>
            <p className="text-slate-600">Here's what's happening with your account today.</p>
          </div>
        </div>

        {/* No verified account notification */}
        {stripeChecked && !anyApproved && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-900 text-base">Connect an Account to Withdraw Funds</h3>
              <p className="text-sm text-amber-800 mt-1">
                You haven't verified any withdrawal account yet. Connect and verify a USD, GBP, or YEN account to start withdrawing your funds.
              </p>
              <button
                onClick={() => navigate('/account-verification')}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Verify Account
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-slate-300 text-sm mb-1">Total Balance</p>
                  <div className="flex items-center gap-3">
                    <h2 className="text-4xl font-bold">
                      {showBalance ? getFormattedBalance() : '••••••'}
                    </h2>
                    <button
                      onClick={() => setShowBalance(!showBalance)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {showBalance ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
                    </button>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-1">
                  <p className="text-xs font-medium">USD</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <button
                  onClick={() => navigate('/send')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-4 transition-all text-center"
                >
                  <Send className="mx-auto mb-2" size={20} />
                  <span className="text-xs font-medium">Send</span>
                </button>
                <button
                  onClick={() => navigate('/receive')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-4 transition-all text-center"
                >
                  <Download className="mx-auto mb-2" size={20} />
                  <span className="text-xs font-medium">Receive</span>
                </button>
                <button
                  onClick={() => navigate('/withdraw')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-4 transition-all text-center"
                >
                  <CreditCard className="mx-auto mb-2" size={20} />
                  <span className="text-xs font-medium">Withdraw</span>
                </button>
                <button
                  onClick={() => navigate('/deposit')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-4 transition-all text-center"
                >
                  <Download className="mx-auto mb-2 rotate-180" size={20} />
                  <span className="text-xs font-medium">Deposit</span>
                </button>
              </div>
            </div>

            {/* Withdrawal Notice */}
            {withdrawalDays > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-900 font-semibold text-center">
                  ⏳ {withdrawalDays} day{withdrawalDays !== 1 ? 's' : ''} until withdrawal available
                </p>
              </div>
            )}

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <button
                onClick={() => window.open('https://crypto.link.com?ref=lb', '_blank')}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">₿</span>
                </div>
                <p className="text-sm font-semibold text-slate-900">Buy Crypto</p>
              </button>

              <button
                onClick={() => navigate('/investment-dashboard')}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
                <p className="text-sm font-semibold text-slate-900">PENE Invest</p>
              </button>

              <button
                onClick={() => navigate('/payment-link')}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Wallet className="text-blue-600" size={24} />
                </div>
                <p className="text-sm font-semibold text-slate-900">Payment Link</p>
              </button>

              <button
                onClick={() => navigate('/referral')}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Users className="text-purple-600" size={24} />
                </div>
                <p className="text-sm font-semibold text-slate-900">Referral</p>
              </button>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
                <button
                  onClick={() => navigate('/history')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View All
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
                  <p className="text-sm text-slate-600 mt-2">Loading transactions...</p>
                </div>
              ) : recentTransactions.length > 0 ? (
                <div className="space-y-3">
                  {recentTransactions.map((tx, idx) => (
                    <div key={idx} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {tx.type === 'credit' ? (
                            <Download className="text-green-600" size={18} />
                          ) : (
                            <Send className="text-red-600" size={18} />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{tx.description}</p>
                          <p className="text-xs text-slate-500">{tx.date}</p>
                        </div>
                      </div>
                      <p className={`font-semibold ${
                        tx.type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="mx-auto mb-3 text-slate-300" size={48} />
                  <p className="text-slate-500">No recent transactions</p>
                  <p className="text-sm text-slate-400 mt-1">Start making transactions to see your activity</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Account Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Account Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Account Type</span>
                  <span className="text-sm font-semibold text-slate-900">Standard</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Status</span>
                  <span className="text-sm font-semibold text-green-600">Active</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total Transactions</span>
                  <span className="text-sm font-semibold text-slate-900">{totalTransactions}</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
              <h3 className="font-semibold text-slate-900 mb-4">This Month</h3>
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Money Sent</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(monthStats.sent)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Money Received</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(monthStats.received)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Support Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center">
                  <Mail size={15} className="text-white" />
                </div>
                <h3 className="font-semibold text-slate-900">Need Help?</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Our support team is available 24/7 to assist you with any questions.
              </p>
              <button
                onClick={() => setShowSupportModal(true)}
                className="w-full py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-all"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>

      {/* Support Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 relative">
            <button
              onClick={() => setShowSupportModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <h2 className="font-bold text-slate-900 text-lg">Contact Support</h2>
                <p className="text-xs text-slate-500">We typically reply within a few hours</p>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {/* WhatsApp */}
              <a
                href="https://whatsapp.com/channel/0029Vb61H0GKwqSXh6HMrN2R"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full bg-green-500 text-white px-4 py-3 rounded-xl hover:bg-green-600 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.113 1.524 5.84L.057 23.999l6.305-1.654A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.8 9.8 0 01-5.007-1.371l-.36-.214-3.733.979 1-3.641-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/></svg>
                <div className="text-left">
                  <p className="text-sm font-semibold">WhatsApp Channel</p>
                  <p className="text-xs text-green-100">Join our official channel</p>
                </div>
              </a>

              {/* Telegram */}
              <a
                href="https://t.me/+51U-Wb22ZUc5MjVk"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full bg-sky-500 text-white px-4 py-3 rounded-xl hover:bg-sky-600 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.19 13.238l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.958.321z"/></svg>
                <div className="text-left">
                  <p className="text-sm font-semibold">Telegram Group</p>
                  <p className="text-xs text-sky-100">Join our community</p>
                </div>
              </a>

              {/* Email button */}
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Support Request&body=Hi Support Team,%0D%0A%0D%0AUser: ${user?.telegramName || ""}%0D%0A%0D%0AIssue: `}
                className="flex items-center gap-3 w-full bg-slate-900 text-white px-4 py-3 rounded-xl hover:bg-slate-800 transition-all"
              >
                <Mail size={18} />
                <div className="text-left">
                  <p className="text-sm font-semibold">Send us an Email</p>
                  <p className="text-xs text-slate-300">{SUPPORT_EMAIL}</p>
                </div>
              </a>

              {/* Copy email button */}
              <button
                onClick={handleCopyEmail}
                className="flex items-center gap-3 w-full border border-slate-200 text-slate-700 px-4 py-3 rounded-xl hover:bg-slate-50 transition-all"
              >
                {emailCopied ? <CheckCheck size={18} className="text-green-600" /> : <Copy size={18} />}
                <div className="text-left">
                  <p className="text-sm font-semibold">{emailCopied ? "Email Copied!" : "Copy Email Address"}</p>
                  <p className="text-xs text-slate-400">{SUPPORT_EMAIL}</p>
                </div>
              </button>
            </div>

            <p className="text-center text-xs text-slate-400 mt-5">
              Support hours: 24/7 &bull; Avg. response &lt; 2 hrs
            </p>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
